import { SsoButtons } from './sso';
import { useT } from '@/lib/i18n';
import { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiRequestError } from '@/lib/api';
import { AuthLayout, FormError } from './auth-layout';
import { useLogin, useSession } from './use-session';

const schema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Enter your password'),
  totp: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  const t = useT();
  const { data: user, isLoading } = useSession();
  const login = useLogin();
  const location = useLocation();
  // Shown only after the server says this account has 2FA switched on.
  const [needsTotp, setNeedsTotp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '', totp: '' },
  });

  if (isLoading) return null;
  if (user) {
    const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';
    return <Navigate to={from} replace />;
  }

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await login.mutateAsync({
        email: values.email,
        password: values.password,
        ...(values.totp ? { totp: values.totp } : {}),
      });
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        if (caught.message === 'TOTP_REQUIRED') {
          setNeedsTotp(true);
          setError(null);
          return;
        }
        if (caught.message === 'TOTP_SETUP_REQUIRED') {
          setError(
            'Your organisation requires two-factor authentication. Ask an administrator to help you set it up.',
          );
          return;
        }
        setError(caught.message);
        return;
      }
      setError('Could not sign in. Please try again.');
    }
  });

  return (
    <AuthLayout
      title="Welcome back"
      description="Sign in to your DovoCRM workspace."
      footer={
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Link to="/forgot-password" className="font-semibold text-link hover:underline">
            {t('Forgot your password?')}
          </Link>
          <span>
            {t('New to DovoCRM?')}{' '}
            <Link to="/signup" className="font-semibold text-link hover:underline">
              {t('Start free trial')}
            </Link>
          </span>
        </div>
      }
    >
      <SsoButtons />
      <form onSubmit={onSubmit} className="mt-4 space-y-5">
        <div className="space-y-1.5">
          <Label htmlFor="email">{t('Work email')}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            autoFocus
            className="h-11 px-3.5"
            aria-invalid={Boolean(form.formState.errors.email)}
            {...form.register('email')}
          />
          {form.formState.errors.email && (
            <p className="text-xs text-destructive">{form.formState.errors.email.message}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password">{t('Password')}</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              className="h-11 px-3.5 pe-11"
              aria-invalid={Boolean(form.formState.errors.password)}
              {...form.register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword((shown) => !shown)}
              className="absolute end-1 top-1/2 -translate-y-1/2 rounded-md p-2 text-muted-foreground transition-colors hover:text-foreground"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {form.formState.errors.password && (
            <p className="text-xs text-destructive">{form.formState.errors.password.message}</p>
          )}
        </div>

        {needsTotp && (
          <div className="space-y-1.5">
            <Label htmlFor="totp">{t('Authentication code')}</Label>
            <Input
              id="totp"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              maxLength={6}
              className="h-11 px-3.5"
              autoFocus
              {...form.register('totp')}
            />
            <p className="text-xs text-muted-foreground">
              Open your authenticator app and enter the current 6-digit code.
            </p>
          </div>
        )}

        <FormError message={error} />

        <Button type="submit" size="lg" className="w-full shadow-lg" disabled={login.isPending}>
          {login.isPending ? t('Signing in…') : t('Sign in')}
          {!login.isPending && <ArrowRight className="h-4 w-4" />}
        </Button>
      </form>
    </AuthLayout>
  );
}
